/**
 * Aether Dashboard Home — role-aware landing view.
 *
 * One component, three layouts:
 *  - admin:    tabs Übersicht / Umsatz / Aktivität + 12 toggleable widgets
 *  - partner:  tabs Übersicht / Performance + 6 toggleable widgets
 *  - customer: single Übersicht + 4 toggleable widgets
 *
 * Each widget can be turned on/off via the Cog button in the top-right.
 * The per-role visibility map is persisted to localStorage so the layout
 * sticks across sessions. Data still comes from a single
 * GET /api/dashboard/me — extra fields just stay unused if hidden.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, TrendingUp, ShoppingCart, Users, AlertCircle, UserPlus,
  DollarSign, Link as LinkIcon, Key, Package, Mail, Sparkles, ArrowRight,
  CheckCircle2, Settings, Eye, EyeOff, Crown, BarChart3, Activity,
  Download, ExternalLink, ChevronDown, Gift, MessagesSquare, Boxes, UserCog, LogOut,
} from 'lucide-react';
import InstallerDownload, { ZxpLink } from '../../components/InstallerDownload';
import { ASSETS, COMMUNITY } from '../../data/portal';
import { AVATARE, Avatar } from '../../data/avatare';
import { useAetherAuth } from './AetherAuthContext';
import { useListControls, ListToolbar } from './listControls';
import OnboardingTour, { startAetherTour } from './OnboardingTour';
import AccountSettingsModal from './AccountSettingsModal';
import FeedbackWidget from './FeedbackWidget';

/* Shop product metadata — image + one-line tagline keyed by the same
 * product_id the backend uses. Lets the customer "Meine Produkte" tab
 * render shop-quality cards without the backend needing to know about
 * images. Images are served LOCALLY from /public/products (never hotlinked
 * from a third-party CDN). Only the three real, buyable extensions belong
 * here — keep in sync with Shop.jsx. */
const PRODUCT_META = {
  'studio-flow': {
    image: '/products/studioflow.png',
    tagline: 'Alle Werkzeuge in einer Erweiterung.',
  },
  'instant-curve': {
    image: '/products/titel/instant-curve.jpg',
    tagline: 'Easing-Kurven auf Keyframes — kostenlos.',
  },
  'bounce-flow': {
    image: '/products/titel/bounce-flow.jpg',
    tagline: 'Federndes Nachschwingen für jede Bewegung.',
  },
  'shake-flow': {
    image: '/products/titel/shake-flow.jpg',
    tagline: 'Kamerawackeln mit einem Klick.',
  },
  'glitch-flow': {
    image: '/products/titel/glitch-flow.jpg',
    tagline: 'Digitale Störungen: RGB-Versatz, Blöcke, Rauschen.',
  },
  'transition-flow': {
    image: '/products/transitions.png',
    tagline: 'Übergänge zwischen Ebenen und Kompositionen.',
  },
  'text-flow': {
    image: '/products/titel/text-flow.jpg',
    tagline: 'Textanimationen, Untertitel und Textboxen.',
  },
  'grid-flow': {
    image: '/products/titel/grid-flow.jpg',
    tagline: 'Raster aus Ebenen — Abstände, Reihen, Spalten.',
  },
  'carousel-flow': {
    image: '/products/titel/carousel-flow.jpg',
    tagline: 'Kreisende Karussells in 2D und 3D.',
  },
  'shape-flow': {
    image: '/products/titel/shape-flow.jpg',
    tagline: 'Formebenen erzeugen und animieren.',
  },
  'background-flow': {
    image: '/products/titel/background-flow.jpg',
    tagline: 'Animierte Hintergründe und Verläufe.',
  },
  'instant-ui-kit': {
    image: '/products/titel/instant-ui-kit.jpg',
    tagline: 'Fertige UI-Elemente und Handy-Screens als echte Ebenen.',
  },
  'media-flow': {
    image: '/products/mediaflow.png',
    tagline: 'Nahtloses Medienmanagement & Workflow für AE.',
  },
  'instant-comp': {
    image: '/products/titel/instant-comp.jpg',
    tagline: 'Eigene Comps als Bausteine sichern und wieder einsetzen.',
  },
  'instant-grade': {
    image: '/products/titel/instant-grade.jpg',
    tagline: 'Color-Grading direkt in After Effects.',
  },
};

// Products a customer might NOT own yet — used for the upsell row.
// Only genuinely buyable extensions belong here. Effect Flow is still
// coming-soon and SFX Grid is free, so neither is an upsell target.
const ALL_SHOP_PRODUCTS = ['studio-flow', 'media-flow', 'instant-grade', 'instant-comp',
  'bounce-flow', 'shake-flow', 'glitch-flow', 'text-flow', 'grid-flow', 'carousel-flow', 'shape-flow', 'background-flow', 'instant-ui-kit'];

const fmtMoney = (n, ccy = 'eur') =>
  `${(Number(n) || 0).toFixed(2)} ${(ccy || 'eur').toUpperCase() === 'EUR' ? '€' : ccy.toUpperCase()}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const relAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'gerade eben';
  const m = Math.floor(s / 60); if (m < 60) return `vor ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `vor ${h} h`;
  const d = Math.floor(h / 24); if (d < 30) return `vor ${d} Tag${d > 1 ? 'en' : ''}`;
  return fmtDate(iso);
};

/* ------------- Widget catalog per role -----------------
 * Each widget belongs to one tab. The Settings cog toggles per widget,
 * persisted in localStorage. Defaults: everything ON.
 */
const ADMIN_TABS    = [
  { id: 'overview', label: 'Übersicht' },
  { id: 'revenue',  label: 'Umsatz' },
  { id: 'licenses', label: 'Lizenzen' },
  { id: 'activity', label: 'Aktivität' },
];
// A partner is also a normal user with purchases, so they get the customer
// tabs (Produkte/Bestellungen) on top of their affiliate tabs. Der Schlüssel
// steht in der Bestellung — dieselbe Ansicht wie im Installer.
const PARTNER_TABS  = [
  { id: 'overview',    label: 'Übersicht',    icon: BarChart3 },
  { id: 'performance', label: 'Performance',  icon: TrendingUp },
  { id: 'products',    label: 'Produkte',     icon: Package },
  { id: 'orders',      label: 'Bestellungen', icon: ShoppingCart },
];
// Bereiche, die zur Person gehoeren und nicht zur Rolle.
const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERSOENLICH = ['assets', 'community', 'profile'];
// Aus der Seitenleiste gesteuerte eigene Seiten — sie bekommen weder die
// Reiterzeile noch das Zahnrad, weil beides woandershin gehoert.
const AUS_LEISTE = [...PERSOENLICH, 'produkte'];

const CUSTOMER_TABS = [
  { id: 'overview',  label: 'Übersicht',    icon: BarChart3 },
  { id: 'products',  label: 'Produkte',     icon: Package },
  { id: 'orders',    label: 'Bestellungen', icon: ShoppingCart },
  { id: 'assets',    label: 'Assets',       icon: Boxes },
  { id: 'community', label: 'Community',    icon: MessagesSquare },
  { id: 'profile',   label: 'Profil',       icon: UserCog },
];

const WIDGETS_ADMIN = {
  bigStats:       { tab: 'overview', label: 'Haupt-Kennzahlen' },
  recentOrders:   { tab: 'overview', label: 'Letzte Bestellungen' },
  newUsers:       { tab: 'overview', label: 'Neue Nutzer' },
  revenueChart:   { tab: 'revenue',  label: 'Umsatz-Verlauf (14 T.)' },
  revenueBigStats:{ tab: 'revenue',  label: 'Umsatz-Kennzahlen' },
  topProducts:    { tab: 'revenue',  label: 'Top-Produkte' },
  topAffiliates:  { tab: 'revenue',  label: 'Top-Affiliates' },
  licenseStats:   { tab: 'licenses', label: 'Lizenz-Pool Kennzahlen' },
  recentLicenses: { tab: 'licenses', label: 'Zuletzt erstellte Lizenzen' },
  activityOrders: { tab: 'activity', label: 'Letzte Bestellungen' },
  activityUsers:  { tab: 'activity', label: 'Neue Nutzer' },
  activityEmails: { tab: 'activity', label: 'Letzte E-Mails' },
};
const WIDGETS_PARTNER = {
  affiliateCard:  { tab: 'overview',    label: 'Affiliate-Karte' },
  bigStats:       { tab: 'overview',    label: 'Haupt-Kennzahlen' },
  recentSales:    { tab: 'overview',    label: 'Letzte Verkäufe' },
  myProducts:     { tab: 'overview',    label: 'Meine Produkte & Lizenzen' },
  perfChart:      { tab: 'performance', label: 'Provisions-Verlauf (14 T.)' },
  topProducts:    { tab: 'performance', label: 'Deine Top-Produkte' },
  perfStats:      { tab: 'performance', label: 'Performance-Kennzahlen' },
};
const WIDGETS_CUSTOMER = {
  updates:    { tab: 'overview', label: 'Update-Hero' },
  bigStats:   { tab: 'overview', label: 'Haupt-Kennzahlen' },
  orders:     { tab: 'overview', label: 'Deine Bestellungen' },
};

function useWidgetVisibility(role) {
  const STORAGE_KEY = `aether_dashboard_widgets_${role}`;
  const all = role === 'admin' ? WIDGETS_ADMIN : role === 'partner' ? WIDGETS_PARTNER : WIDGETS_CUSTOMER;
  const defaults = useMemo(() => Object.keys(all).reduce((a, k) => (a[k] = true, a), {}), [all]);
  const [visible, setVisible] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      return { ...defaults, ...stored };
    } catch { return defaults; }
  });

  // The role isn't known on first render (`/dashboard/me` hasn't answered yet),
  // so the state above is seeded with the CUSTOMER catalog. Once the real role
  // arrives, both the storage key and the widget catalog change — without this
  // re-sync every widget that only exists for that role stays `undefined` and
  // therefore hidden. That's what made the whole partner dashboard (affiliate
  // card, sales, performance tab) disappear except the one shared key.
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      setVisible({ ...defaults, ...stored });
    } catch {
      setVisible(defaults);
    }
  }, [STORAGE_KEY, defaults]);
  const set = (key, val) => setVisible(prev => {
    const next = { ...prev, [key]: val };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
    return next;
  });
  const resetDefaults = () => {
    setVisible(defaults);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };
  return { visible, set, resetDefaults, all };
}

export default function DashboardHome({ onJumpToTab, customerTab, onCustomerTab }) {
  const { authFetch, user } = useAetherAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const settingsRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr('');
    authFetch('/api/dashboard/me')
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled) return;
        if (!ok) throw new Error(d.detail || 'Fehler beim Laden');
        setData(d);
      })
      .catch(e => !cancelled && setErr(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [authFetch]);

  // Close settings popover on outside click
  useEffect(() => {
    if (!showSettings) return;
    const onDown = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showSettings]);

  const role = data?.role || 'customer';
  const tabs = role === 'admin' ? ADMIN_TABS : role === 'partner' ? PARTNER_TABS : CUSTOMER_TABS;
  const widgetCfg = useWidgetVisibility(role);

  // Customers AND partners drive their tab from the outer Aether sidebar;
  // only admins use this component's own inline tab state.
  const activeTab = (role === 'customer' || role === 'partner')
    ? (customerTab || 'overview')
    // Admins fahren ihre Reiter ueber den eigenen Zustand — ausser bei den drei
    // persoenlichen Bereichen, die aus der Seitenleiste kommen.
    : (AUS_LEISTE.includes(customerTab) ? customerTab : tab);

  // Filter widgets to those belonging to the active tab
  const visibleHere = useMemo(() => {
    const out = {};
    for (const key of Object.keys(widgetCfg.all)) {
      if (widgetCfg.all[key].tab === activeTab && widgetCfg.visible[key]) out[key] = true;
    }
    return out;
  }, [widgetCfg.visible, widgetCfg.all, activeTab]);

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-white/40"><Loader2 size={20} className="animate-spin" /></div>;
  }
  if (err) {
    return <div className="p-4 rounded-lg bg-red-500/10 text-red-300 flex items-center gap-2 text-sm"><AlertCircle size={16} /> {err}</div>;
  }

  const firstName = (user?.name || user?.email || '').split(' ')[0].split('@')[0];
  const tabsForCurrentRole = tabs;

  return (
    <div className="w-full space-y-6" data-testid="dashboard-home">
      {/* Welcome header */}
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30 mb-2">David Media Portal</p>
          <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-white">
            Willkommen{firstName ? `, ${firstName}` : ''}.
          </h1>
          <p className="text-white/50 text-sm mt-2">
            {/* Auf den persoenlichen Seiten passt der Rollen-Satz nicht — dort
                steht, worum es auf DIESER Seite geht. */}
            {activeTab === 'assets'    && 'Zubehör zum Direkt-Laden — gehört zu keiner Lizenz.'}
            {activeTab === 'community' && 'Neues zuerst, Hilfe bei Problemen, und was andere daraus machen.'}
            {activeTab === 'profile'   && 'Dein Konto bei David Media.'}
            {activeTab === 'produkte'  && 'Was du verkaufst, mit den Zahlen dahinter.'}
            {!AUS_LEISTE.includes(activeTab) && role === 'admin'    && 'Hier ist dein operatives Cockpit — alles auf einem Blick.'}
            {!AUS_LEISTE.includes(activeTab) && role === 'partner'  && 'Deine Affiliate-Performance der letzten 30 Tage.'}
            {!AUS_LEISTE.includes(activeTab) && role === 'customer' && 'Hier findest du deine Bestellungen, Lizenz-Keys und Updates.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Feedback lives here in the header row with Tour/Konto/Kunde —
              on one line, opposite the welcome text — instead of floating. */}
          {(role === 'customer' || role === 'partner') && <FeedbackWidget variant="inline" />}
          {/* Customer number — shown for everyone who has one. Reads from
              the auth context user (serialized with customer_number). */}
          {user?.customer_number && (
            <span className="px-3 py-1.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-white/5 text-white/40"
                  title="Deine Kundennummer">
              Nr. {user.customer_number}
            </span>
          )}
          {(role === 'customer' || role === 'partner') && (
            <button
              onClick={startAetherTour}
              className="px-3 py-1.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-white/5 text-white/50 hover:text-white hover:bg-white/10 transition inline-flex items-center gap-1.5"
              title="Kurze Tour durch das Dashboard"
            >
              <Sparkles size={11} /> Tour
            </button>
          )}
          <button
            onClick={() => setShowAccount(true)}
            className="px-3 py-1.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-white/5 text-white/50 hover:text-white hover:bg-white/10 transition inline-flex items-center gap-1.5"
            title="Konto-Einstellungen"
          >
            <Settings size={11} /> Konto
          </button>
          <span className="px-3 py-1.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-white/5 text-white/50">
            {role === 'admin' ? 'Admin' : role === 'partner' ? 'Partner' : 'Kunde'}
          </span>
          <Avatar id={user?.avatar} size={30} />
        </div>
      </header>

      {(role === 'customer' || role === 'partner') && <OnboardingTour role={role} />}
      <AccountSettingsModal open={showAccount} onClose={() => setShowAccount(false)} />

      {/* Tabs row + Settings cog — hidden for customers (they use the left
          sidebar below). Partners also navigate via the sidebar now, so for
          them we keep the row purely to host the widget cog.
          Auf den persoenlichen Seiten (Assets, Community, Profil) hat beides
          nichts zu suchen: die Reiter fuehren woandershin und das Zahnrad
          schaltet Widgets, die es dort gar nicht gibt. */}
      <div data-tour="tabs" className={`${role === 'customer' || AUS_LEISTE.includes(activeTab) ? 'hidden' : 'flex'} items-center justify-between gap-2 border-b`} style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="flex">
          {role === 'admin' && tabsForCurrentRole.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px transition ${
                tab === t.id ? 'text-white border-white' : 'text-white/40 border-transparent hover:text-white/70'
              }`}
              data-testid={`dashboard-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Settings cog → widget toggle popover */}
        <div className="relative pb-2" ref={settingsRef}>
          <button
            onClick={() => setShowSettings(s => !s)}
            className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition"
            title="Widgets ein-/ausblenden"
            data-testid="dashboard-settings-btn"
          >
            <Settings size={14} className={showSettings ? 'text-white' : ''} />
          </button>
          {showSettings && (
            <SettingsPopover
              widgetCfg={widgetCfg}
              tabs={tabsForCurrentRole}
              activeTab={activeTab}
              onClose={() => setShowSettings(false)}
            />
          )}
        </div>
      </div>

      {/* Tab body */}
      {/* Persoenliche Bereiche: gelten fuer jede Rolle. Ein Admin ist auch eine
          Person mit Konto — vorher hatte er kein Profil und keinen Zugriff auf
          Assets und Community. */}
      {activeTab === 'assets'    && <CustomerAssets />}
      {activeTab === 'community' && <CustomerCommunity />}
      {activeTab === 'profile'   && <CustomerProfile />}
      {activeTab === 'produkte'  && role === 'admin' && <AdminProdukte />}

      {role === 'admin' && !AUS_LEISTE.includes(activeTab) && <AdminView    data={data} visible={visibleHere} onJumpToTab={onJumpToTab} />}
      {role === 'partner' && !AUS_LEISTE.includes(activeTab) && <PartnerView  data={data} visible={visibleHere} onJumpToTab={onJumpToTab} tab={activeTab} />}
      {/* Customer + partner tabs live in the OUTER Aether sidebar
          (AetherDashboard) — here we just render the selected tab. */}
      {role === 'customer' && (
        <CustomerView data={data} tab={activeTab} onTab={onCustomerTab} />
      )}
    </div>
  );
}

/* ============================== SETTINGS POPOVER ============================== */
function SettingsPopover({ widgetCfg, tabs, activeTab, onClose }) {
  // Group widgets by tab so the user can scan their layout quickly
  const grouped = useMemo(() => {
    const g = {};
    Object.entries(widgetCfg.all).forEach(([key, meta]) => {
      (g[meta.tab] = g[meta.tab] || []).push({ key, ...meta });
    });
    return g;
  }, [widgetCfg.all]);

  return (
    <div
      className="absolute right-0 top-full mt-2 z-50 rounded-xl shadow-2xl"
      style={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', minWidth: 280 }}
    >
      <div className="flex items-center justify-between p-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/60">Widgets</p>
        <button
          onClick={() => { widgetCfg.resetDefaults(); }}
          className="font-mono text-[9px] uppercase tracking-widest text-white/40 hover:text-white"
          title="Alle Widgets wieder einblenden"
        >
          Zurücksetzen
        </button>
      </div>
      <div className="max-h-80 overflow-y-auto aether-scrollbar p-2">
        {tabs.map(t => (
          <div key={t.id} className="mb-2 last:mb-0">
            <p className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 ${t.id === activeTab ? 'text-orange-300' : 'text-white/30'}`}>
              {t.label}
            </p>
            {(grouped[t.id] || []).map(w => (
              <label
                key={w.key}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/5 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!!widgetCfg.visible[w.key]}
                  onChange={(e) => widgetCfg.set(w.key, e.target.checked)}
                  className="accent-orange-500"
                />
                <span className="text-xs text-white/70 flex-1">{w.label}</span>
                {widgetCfg.visible[w.key] ? <Eye size={11} className="text-white/30" /> : <EyeOff size={11} className="text-white/20" />}
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================== ADMIN VIEW ============================== */
function AdminView({ data, visible, onJumpToTab }) {
  const { stats, recent_orders, top_products, top_affiliates, new_users, revenue_series, recent_emails, license_stats, recent_licenses } = data;
  const hasContent = Object.keys(visible).length > 0;

  return (
    <div className="space-y-6">
      {!hasContent && <NothingVisibleHint />}


      {visible.bigStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <BigStat label="Umsatz heute"     value={fmtMoney(stats.revenue_today)} icon={DollarSign}   accent="text-emerald-400" />
          <BigStat label="Umsatz 30 Tage"   value={fmtMoney(stats.revenue_30d)}   icon={TrendingUp}   accent="text-orange-400" />
          <BigStat label="Bestellungen ges."value={stats.orders_total}            icon={ShoppingCart} accent="text-sky-400" />
          <BigStat label="Nutzer (24h)"     value={`+${stats.new_users_24h}`}     icon={Users}        accent="text-violet-400" subtitle={`${stats.users_total} insgesamt`} />
        </div>
      )}

      {visible.recentOrders && (
        <Section title="Letzte Bestellungen" cta={{ label: 'Alle Bestellungen', onClick: () => onJumpToTab && onJumpToTab('orders') }}>
          <OrdersList orders={recent_orders} showCustomer />
        </Section>
      )}

      {visible.newUsers && (
        <Section title="Neue Nutzer" cta={{ label: 'Nutzerverwaltung', onClick: () => onJumpToTab && onJumpToTab('users') }}>
          <NewUsersList users={new_users} />
        </Section>
      )}

      {/* === Tab: Umsatz === */}
      {visible.revenueBigStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <BigStat label="Heute"    value={fmtMoney(stats.revenue_today)} icon={DollarSign}  accent="text-emerald-400" />
          <BigStat label="7 Tage"   value={fmtMoney(stats.revenue_7d)}    icon={TrendingUp}  accent="text-orange-400" />
          <BigStat label="30 Tage"  value={fmtMoney(stats.revenue_30d)}   icon={TrendingUp}  accent="text-violet-400" />
          <BigStat label="Ø / Tag (30 T.)" value={fmtMoney((stats.revenue_30d || 0) / 30)} icon={BarChart3} accent="text-sky-400" />
        </div>
      )}

      {visible.revenueChart && <RevenueSparkline series={revenue_series} title="Umsatz · letzte 14 Tage" valueKey="revenue" />}
      {visible.topProducts   && <TopProductsList products={top_products} mode="revenue" />}
      {visible.topAffiliates && <TopAffiliatesList affiliates={top_affiliates} />}

      {/* === Tab: Lizenzen === */}
      {visible.licenseStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <BigStat label="Lizenzen gesamt"  value={license_stats?.total ?? 0}        icon={Key}        accent="text-sky-400" />
          <BigStat label="Aktiv"            value={license_stats?.active ?? 0}       icon={CheckCircle2} accent="text-emerald-400" />
          <BigStat label="Deaktiviert"      value={license_stats?.deactivated ?? 0}  icon={EyeOff}     accent="text-amber-400" />
          <BigStat label="Aktivierungen"    value={license_stats?.activations ?? 0}  icon={Activity}   accent="text-violet-400" />
        </div>
      )}
      {visible.recentLicenses && (
        <Section title="Zuletzt erstellte Lizenzen" cta={{ label: 'Lizenz-Verwaltung', onClick: () => onJumpToTab && onJumpToTab('licenses') }}>
          <RecentLicensesList licenses={recent_licenses} />
        </Section>
      )}

      {/* === Tab: Aktivität === */}
      {visible.activityOrders && (
        <Section title="Letzte Bestellungen">
          <OrdersList orders={recent_orders} showCustomer />
        </Section>
      )}
      {visible.activityUsers && (
        <Section title="Neue Nutzer">
          <NewUsersList users={new_users} />
        </Section>
      )}
      {visible.activityEmails && (
        <Section title="Letzte E-Mails" cta={{ label: 'E-Mail-Log', onClick: () => onJumpToTab && onJumpToTab('emails') }}>
          {(!recent_emails || recent_emails.length === 0) ? (
            <Empty label="Noch keine E-Mails versendet." />
          ) : (
            <div className="space-y-2">
              {recent_emails.map((e, i) => (
                <div key={i} className="monolith-glass p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{e.subject || '—'}</p>
                    <p className="mt-1 font-mono text-[10px] text-white/40 flex items-center gap-2">
                      <Mail size={10} />{e.to || '—'} · {relAgo(e.created_at)}
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest ${
                    (e.status === 'sent' || e.status === 'delivered') ? 'bg-emerald-400/10 text-emerald-300'
                    : e.status === 'failed' ? 'bg-red-400/10 text-red-300'
                    : 'bg-white/5 text-white/40'
                  }`}>{e.status || '—'}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

/* ============================== PARTNER VIEW ============================== */
function PartnerView({ data, visible, onJumpToTab, tab }) {
  const { affiliate, stats, recent_sales, revenue_series, top_products } = data;

  // Partners are customers too — these three tabs reuse the customer views
  // verbatim so a partner can reach their own products, keys and purchases.
  if (tab === 'products') {
    return <CustomerProducts products={data.products || []} />;
  }
  if (tab === 'orders') {
    return <CustomerOrdersList orders={data.orders || []} products={data.products || []} />;
  }

  const hasContent = Object.keys(visible).length > 0;
  return (
    <div className="space-y-6">
      {!hasContent && <NothingVisibleHint />}

      {visible.affiliateCard && (
        affiliate ? (
          <div data-tour="affiliate" className="monolith-glass p-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-orange-400/10">
                <LinkIcon size={20} className="text-orange-400" />
              </div>
              <div>
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mb-1">Dein Affiliate-Code</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="font-mono text-base text-orange-300 bg-orange-300/10 px-3 py-1 rounded">{affiliate.code}</code>
                  <span className="text-[10px] font-mono text-white/40">{Math.round((affiliate.commission_rate || 0) * 100)}% Provision</span>
                </div>
                <p className="font-mono text-[10px] text-white/40 mt-2 truncate">{window.location.origin}?ref={affiliate.code}</p>
              </div>
            </div>
            <CopyLinkBtn code={affiliate.code} />
          </div>
        ) : (
          <div className="p-4 rounded-xl flex items-start gap-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
            <AlertCircle size={14} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs leading-relaxed text-white/70">
              Du hast noch keinen Affiliate-Code. Bitte einen Admin um Aktivierung deines Partner-Programms.
            </p>
          </div>
        )
      )}

      {visible.bigStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <BigStat label="Verkäufe (30 T.)"    value={stats.sales_30d}                          icon={ShoppingCart} accent="text-sky-400" subtitle={`${stats.sales_total} insgesamt`} />
          <BigStat label="Umsatz (30 T.)"      value={fmtMoney(stats.revenue_30d)}              icon={DollarSign}   accent="text-emerald-400" />
          <BigStat label="Provision (30 T.)"   value={fmtMoney(stats.commission_30d)}           icon={TrendingUp}   accent="text-orange-400" />
          <BigStat label="Provision gesamt"    value={fmtMoney(stats.commission_total)}         icon={DollarSign}   accent="text-violet-400" />
        </div>
      )}

      {visible.recentSales && <PartnerSalesList orders={recent_sales} />}

      {visible.myProducts && <PartnerLibrary items={data.library} />}

      {/* Performance tab */}
      {visible.perfChart && <RevenueSparkline series={revenue_series} title="Provision · letzte 14 Tage" valueKey="revenue" />}
      {visible.perfStats && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <BigStat label="Verkäufe gesamt"    value={stats.sales_total}                 icon={ShoppingCart} accent="text-sky-400" />
          <BigStat label="Provision gesamt"   value={fmtMoney(stats.commission_total)}  icon={DollarSign}   accent="text-emerald-400" />
          <BigStat label="Ø Provision / Sale" value={fmtMoney(stats.sales_total ? (stats.commission_total || 0) / stats.sales_total : 0)} icon={TrendingUp} accent="text-orange-400" />
        </div>
      )}
      {visible.topProducts && <TopProductsList products={top_products} mode="commission" />}
    </div>
  );
}

/* ============================== CUSTOMER VIEW ============================== */
function QuickTile({ icon: Icon, label, hint, onClick }) {
  return (
    <button onClick={onClick} className="monolith-glass p-4 flex items-center gap-3 text-left hover:bg-white/5 transition group w-full">
      <div className="p-2.5 rounded-xl bg-white/5 group-hover:bg-orange-400/10 transition shrink-0">
        <Icon size={18} className="text-white/60 group-hover:text-orange-300 transition" />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-white font-medium">{label}</p>
        <p className="text-[11px] text-white/40 mt-0.5">{hint}</p>
      </div>
      <ArrowRight size={15} className="text-white/20 ml-auto group-hover:text-white/50 transition shrink-0" />
    </button>
  );
}

/* Update hero — surfaces the REAL "what's new" for the customer's most
 * recently acquired product that ships a changelog. The backend sends
 * version + changelog per owned product; this used to be discarded in
 * favour of a hardcoded "everything up to date" line. Falls back to that
 * reassuring message when there's genuinely nothing to show. */
function UpdateHero({ products }) {
  const withNotes = (products || []).filter(p => p && p.changelog && p.changelog.trim());
  const feat = withNotes[0]; // products arrive sorted newest-owned first
  const note = feat ? feat.changelog.trim() : '';
  const shortNote = note.length > 240 ? note.slice(0, 240).trimEnd() + '…' : note;

  return (
    <div className="monolith-glass p-5 flex items-start gap-4 flex-wrap"
         style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.06) 0%, rgba(255,255,255,0.02) 100%)' }}>
      <div className="p-3 rounded-xl bg-orange-400/10 shrink-0">
        <Sparkles size={20} className="text-orange-400" />
      </div>
      <div className="flex-1 min-w-0">
        {feat ? (
          <>
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/30">Was ist neu</p>
              <span className="font-mono text-[9px] text-orange-300 bg-orange-300/10 px-1.5 py-0.5 rounded">
                {feat.product_name} · v{feat.version}
              </span>
            </div>
            <p className="text-sm text-white/80 whitespace-pre-line">{shortNote}</p>
            {feat.download_url && (
              <ZxpLink href={feat.download_url}
                 className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest text-black transition hover:scale-[1.03]"
                 style={{ background: 'var(--primary, #F97316)' }}>
                <Download size={12} /> v{feat.version} laden
              </ZxpLink>
            )}
          </>
        ) : (
          <>
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mb-1">Updates</p>
            <p className="text-sm text-white">Alle deine Extensions sind auf dem neuesten Stand.</p>
            <p className="text-xs text-white/50 mt-1">Neue Versionen erscheinen automatisch hier und kommen per E-Mail.</p>
          </>
        )}
      </div>
    </div>
  );
}

/* =========================== ASSETS / COMMUNITY / PROFIL ===========================
   Dieselben drei Bereiche, die der Installer hat. Wer am Handy oder am fremden
   Rechner nachsieht, kam bisher an die Gratis-Downloads und den Discord nicht heran.
   Die Listen stehen in src/data/portal.js — dieselben Daten wie in der App. */

function CustomerAssets() {
  return (
    <div className="space-y-4" data-testid="customer-assets">
      <div>
        <h2 className="text-white text-lg font-medium">Assets</h2>
        <p className="text-white/50 text-sm mt-1">
          Zubehör zum Direkt-Laden — gehört zu keiner Lizenz, du brauchst nichts freizuschalten.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
        {ASSETS.map((a) => (
          <a
            key={a.id}
            href={a.url}
            target="_blank"
            rel="noreferrer"
            className="monolith-glass overflow-hidden flex flex-col group"
            data-testid={`asset-${a.id}`}
          >
            <div className="relative aspect-[16/9] overflow-hidden bg-black/40">
              {a.bild && (
                <img
                  src={a.bild}
                  alt={a.name}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-70 transition-all duration-500 group-hover:opacity-100 group-hover:scale-105"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <span className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-400/15 text-emerald-300">
                {a.preis}
              </span>
              <div className="absolute bottom-3 left-4 right-4">
                <h3 className="font-syne font-bold text-base text-white leading-tight">{a.name}</h3>
                <p className="font-mono text-[9px] text-white/60 mt-1 truncate">{a.beschreibung}</p>
              </div>
            </div>
            <div className="p-3 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">Herunterladen</span>
              <Download size={14} className="text-orange-400 transition-transform group-hover:translate-y-0.5" />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function CustomerCommunity() {
  return (
    <div className="space-y-4" data-testid="customer-community">
      <div>
        <h2 className="text-white text-lg font-medium">Community</h2>
        <p className="text-white/50 text-sm mt-1">
          Neues zuerst, Hilfe bei Problemen, und was andere aus den Werkzeugen machen.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {COMMUNITY.map((c) => {
          const inhalt = (
            <>
              <div className="flex items-center justify-between gap-3">
                <span className="text-white font-medium">{c.name}</span>
                {c.url
                  ? <ExternalLink size={13} className="text-white/30 shrink-0" />
                  : <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 text-white/40 shrink-0">bald</span>}
              </div>
              <p className="text-white/50 text-sm">{c.text}</p>
            </>
          );
          return c.url ? (
            <a
              key={c.id}
              href={c.url}
              target="_blank"
              rel="noreferrer"
              className="monolith-glass p-5 flex flex-col gap-2 hover:border-white/20 transition"
              data-testid={`community-${c.id}`}
            >{inhalt}</a>
          ) : (
            <div
              key={c.id}
              className="monolith-glass p-5 flex flex-col gap-2 opacity-50"
              data-testid={`community-${c.id}`}
            >{inhalt}</div>
          );
        })}
      </div>
    </div>
  );
}

function ProfilZeile({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-white/5 last:border-b-0">
      <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{label}</span>
      <span className="text-sm text-white/80 text-right break-all">{children}</span>
    </div>
  );
}

function CustomerProfile() {
  const { user, updateProfile, requestPasswordReset, logout, deleteAccount } = useAetherAuth();
  const [name, setName] = useState(user?.name || '');
  const [nameBusy, setNameBusy] = useState(false);
  const [meldung, setMeldung] = useState(null);
  const [pwBusy, setPwBusy] = useState(false);
  const [loeschOffen, setLoeschOffen] = useState(false);
  const [loeschWort, setLoeschWort] = useState('');
  const [loeschBusy, setLoeschBusy] = useState(false);

  useEffect(() => { setName(user?.name || ''); }, [user]);

  const unveraendert = name.trim() === (user?.name || '').trim();

  const bildWaehlen = async (id) => {
    setMeldung(null);
    try { await updateProfile({ avatar: id }); }
    catch (e) { setMeldung({ ok: false, text: e.message || 'Bild konnte nicht gespeichert werden.' }); }
  };

  const speichern = async () => {
    setNameBusy(true); setMeldung(null);
    try {
      await updateProfile({ name: name.trim() });
      setMeldung({ ok: true, text: 'Name gespeichert.' });
    } catch (e) {
      setMeldung({ ok: false, text: e.message || 'Konnte nicht gespeichert werden.' });
    } finally { setNameBusy(false); }
  };

  const passwortMail = async () => {
    setPwBusy(true); setMeldung(null);
    try {
      await requestPasswordReset(user?.email);
      setMeldung({ ok: true, text: 'E-Mail verschickt — sieh auch im Spam-Ordner nach.' });
    } catch (e) {
      setMeldung({ ok: false, text: e.message || 'Konnte die E-Mail nicht senden.' });
    } finally { setPwBusy(false); }
  };

  const loeschen = async () => {
    setLoeschBusy(true); setMeldung(null);
    try {
      await deleteAccount(loeschWort.trim());
      // clearSession() im Context wirft uns zurück auf die Anmeldung.
    } catch (e) {
      setMeldung({ ok: false, text: e.message || 'Konto konnte nicht gelöscht werden.' });
      setLoeschBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="customer-profile">
      <div>
        <h2 className="text-white text-lg font-medium">Profil</h2>
        <p className="text-white/50 text-sm mt-1">Dein Konto bei David Media.</p>
      </div>

      <div className="monolith-glass p-5">
        <div className="flex items-center gap-4 mb-4">
          <Avatar id={user?.avatar} size={64} />
          <div className="min-w-0">
            <p className="text-white text-lg font-medium truncate">
              {user?.name || (user?.email || '').split('@')[0]}
            </p>
            <p className="text-white/45 text-sm truncate">{user?.email}</p>
          </div>
        </div>
        <ProfilZeile label="E-Mail">{user?.email || '—'}</ProfilZeile>
        {user?.customer_number && <ProfilZeile label="Kundennummer">{user.customer_number}</ProfilZeile>}
        {user?.partner_number && <ProfilZeile label="Partnernummer">{user.partner_number}</ProfilZeile>}
        <ProfilZeile label="Rolle">
          {user?.role === 'admin' ? 'Admin' : user?.role === 'partner' ? 'Partner' : 'Kunde'}
        </ProfilZeile>
      </div>

      <div className="monolith-glass p-5 space-y-3">
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40">Profilbild</h3>
        <p className="text-white/45 text-sm">Wird auch im Installer angezeigt. Eigene Bilder gibt es bewusst nicht.</p>
        <div className="flex flex-wrap gap-2.5">
          {AVATARE.map(a => (
            <button
              key={a.id}
              onClick={() => bildWaehlen(a.id)}
              title={a.id}
              className={`rounded-full p-0.5 border-2 transition hover:scale-105 ${
                user?.avatar === a.id ? 'border-orange-400' : 'border-transparent'}`}
              data-testid={`avatar-${a.id}`}
            >
              <Avatar id={a.id} size={44} />
            </button>
          ))}
        </div>
      </div>

      <div className="monolith-glass p-5 space-y-3">
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40">Name</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Vor- und Nachname"
            className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-white/25 placeholder:text-white/25"
            data-testid="profile-name-input"
          />
          <button
            onClick={speichern}
            disabled={nameBusy || unveraendert || !name.trim()}
            className="px-4 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest text-black bg-white transition hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
            data-testid="profile-name-save"
          >
            {nameBusy ? 'Speichert' : 'Speichern'}
          </button>
        </div>
      </div>

      <div className="monolith-glass p-5 space-y-3">
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40">Passwort</h3>
        <p className="text-white/50 text-sm">
          Wir schicken dir einen Link an {user?.email || 'deine Adresse'}. Er gilt 30 Minuten.
        </p>
        <button
          onClick={passwortMail}
          disabled={pwBusy}
          className="px-4 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest text-white/70 bg-white/5 border border-white/10 hover:text-white hover:bg-white/10 transition disabled:opacity-40"
          data-testid="profile-password-btn"
        >
          {pwBusy ? 'Wird gesendet' : 'Passwort ändern'}
        </button>
      </div>

      {meldung && (
        <p className={`text-xs flex items-center gap-1.5 ${meldung.ok ? 'text-emerald-300' : 'text-red-300'}`} data-testid="profile-message">
          {meldung.ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />} {meldung.text}
        </p>
      )}

      <button
        onClick={logout}
        className="px-4 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-red-300 transition inline-flex items-center gap-2"
        data-testid="profile-logout"
      >
        <LogOut size={12} /> Abmelden
      </button>

      {/* Konto löschen. Bewusst am Ende, hinter einem Tippschritt — und nicht
          für Admins, die sich sonst selbst aussperren könnten. */}
      {user?.role !== 'admin' && (
        <div className="monolith-glass p-5 space-y-3 border-red-500/20">
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-red-300/70">Konto löschen</h3>
          <p className="text-white/50 text-sm">
            Dein Zugang wird entfernt. Deine Bestellungen und Rechnungen bleiben —
            dazu sind wir gesetzlich verpflichtet. Mit derselben E-Mail kannst du
            dich später neu registrieren und siehst deine Käufe wieder.
          </p>
          {!loeschOffen ? (
            <button
              onClick={() => { setLoeschOffen(true); setMeldung(null); }}
              className="px-4 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest text-red-300/80 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 transition"
              data-testid="profile-delete-open"
            >
              Konto löschen
            </button>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={loeschWort}
                onChange={(e) => setLoeschWort(e.target.value)}
                placeholder="LÖSCHEN eingeben"
                className="flex-1 bg-white/[0.03] border border-red-500/25 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-red-400/50 placeholder:text-white/25"
                data-testid="profile-delete-input"
              />
              <button
                onClick={loeschen}
                disabled={loeschBusy || loeschWort.trim().toUpperCase() !== 'LÖSCHEN'}
                className="px-4 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest text-white bg-red-600/80 hover:bg-red-600 transition disabled:opacity-40"
                data-testid="profile-delete-confirm"
              >
                {loeschBusy ? 'Löscht' : 'Endgültig löschen'}
              </button>
              <button
                onClick={() => { setLoeschOffen(false); setLoeschWort(''); }}
                className="px-4 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white transition"
              >
                Abbrechen
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ================================ ABO ================================ */
/* Laufendes Abo mit Kuendigungsmoeglichkeit. Der Knopf fuehrt ins Stripe-
   Kundenportal — dort aendert man Zahlungsmittel, sieht Rechnungen und kann
   kuendigen. In Deutschland ist ein leicht auffindbarer Kuendigungsweg
   Pflicht (§ 312k BGB), und Stripe erledigt ihn samt Bestaetigung. */
function AboKarte() {
  const [abo, setAbo] = useState(null);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('aether_token');
    if (!token) return;
    fetch(`${API_URL}/api/me/abo`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => d && setAbo(d.abo))
      .catch(() => {});
  }, []);

  if (!abo) return null;

  const bis = abo.laeuft_bis ? fmtDate(abo.laeuft_bis) : '—';
  const aktiv = abo.status === 'aktiv';

  const portalOeffnen = async () => {
    setLaedt(true);
    try {
      const token = localStorage.getItem('aether_token');
      const r = await fetch(`${API_URL}/api/me/abo-portal`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.url) window.location.href = d.url;
      else throw new Error();
    } catch {
      setLaedt(false);
      alert('Das Kundenportal ist gerade nicht erreichbar. Bitte später noch einmal.');
    }
  };

  return (
    <div className="monolith-glass p-5 rounded-2xl" data-testid="abo-karte">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">Dein Abo</p>
          <p className="text-white text-lg font-semibold mt-1">{abo.produkt}</p>
          <p className="text-white/50 text-sm mt-0.5">
            {aktiv ? `Läuft bis ${bis} · verlängert sich automatisch` : `Beendet · noch nutzbar bis ${bis}`}
          </p>
        </div>
        <button
          onClick={portalOeffnen}
          disabled={laedt}
          className="px-5 py-2.5 rounded-full font-semibold text-sm border border-white/15 text-white hover:bg-white/10 transition-colors disabled:opacity-50"
          data-testid="abo-verwalten"
        >
          {laedt ? 'Öffnet…' : 'Abo verwalten oder kündigen'}
        </button>
      </div>
    </div>
  );
}

function CustomerView({ data, tab, onTab }) {
  const { stats, orders } = data;

  // "Meine Produkte" tab — product-centric library with shop-quality cards.
  if (tab === 'products') {
    return <CustomerProducts products={data.products || []} />;
  }

  // "Bestellungen" tab — Kauf, Schlüssel, Download und Rechnung in einer Zeile.
  if (tab === 'orders') {
    return <CustomerOrdersList orders={orders} products={data.products || []} />;
  }

  // Drei Bereiche, die es bisher nur im Installer gab.

  // "Übersicht" tab (default) — landing: update hero, key stats, quick access.
  return (
    <div className="space-y-6">
      <UpdateHero products={data.products} />
      {/* Der runde Weg: Installer statt einzelner .zxp — der Launcher fuer alles. */}
      <InstallerDownload kompakt />
      <AboKarte />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <BigStat label="Bestellungen"   value={stats.orders_total}          icon={Package}    accent="text-sky-400" />
        <BigStat label="Lizenzen"       value={stats.licenses_total}        icon={Key}        accent="text-orange-400" />
        <BigStat label="Gesamtausgaben" value={fmtMoney(stats.spent_total)} icon={DollarSign} accent="text-emerald-400" />
      </div>

      <div data-tour="cust-quick" className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <QuickTile icon={Package}      label="Produkte" hint="Downloads & Lizenzen" onClick={() => onTab && onTab('products')} />
        <QuickTile icon={ShoppingCart} label="Bestellungen"   hint="Schlüssel, Download, Rechnung" onClick={() => onTab && onTab('orders')} />
        <QuickTile icon={Boxes}        label="Assets"         hint="Gratis zum Laden"     onClick={() => onTab && onTab('assets')} />
      </div>
    </div>
  );
}

/* ============================== CUSTOMER PRODUCTS ============================== */
function CustomerProducts({ products }) {
  const [openId, setOpenId] = useState(null);
  const ownedIds = new Set(products.map(p => p.product_id));
  const upsell = ALL_SHOP_PRODUCTS.filter(id => !ownedIds.has(id));
  const hasOwned = products.length > 0;

  return (
    <div className="space-y-8">
      <InstallerDownload kompakt />
      {/* Eigene Produkte.
          Der grosse Leer-Kasten ("… im Shop findest du alle Extensions")
          erscheint NICHT mehr, wenn direkt darunter ohnehin die Shop-Produkte
          stehen — das widersprach sich: oben "keine Produkte, geh in den Shop",
          unten die Shop-Produkte. Ohne Besitz uebernimmt die Sektion unten die
          Aussage und heisst dann "Alle Extensions" statt "Entdecke mehr". */}
      {hasOwned ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
          {products.map((p, idx) => (
            <ProductCard
              key={p.product_id || idx}
              product={p}
              open={openId === idx}
              onToggle={() => setOpenId(openId === idx ? null : idx)}
            />
          ))}
        </div>
      ) : upsell.length === 0 ? (
        <Empty label="Noch keine Produkte." />
      ) : null}

      {/* Upsell — products the customer doesn't own yet */}
      {upsell.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} className="text-orange-400" />
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              {hasOwned ? 'Entdecke mehr' : 'Alle Extensions'}
            </h3>
          </div>
          <p className="text-[12px] text-white/35 mb-3">
            {hasOwned
              ? 'Diese Extensions hast du noch nicht.'
              : 'Du besitzt noch keine Extensions — das gibt es im Shop:'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upsell.map(id => (
              <UpsellCard key={id} productId={id} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, open, onToggle }) {
  const meta = PRODUCT_META[product.product_id] || {};
  const activeLicenses = product.licenses.filter(l => l.status === 'active');
  const shopUrl = `/shop/${product.product_id}`;

  return (
    <div className="monolith-glass overflow-hidden flex flex-col">
      {/* Header — image + name, click to expand */}
      <button onClick={onToggle} className="text-left group" data-testid={`dash-product-${product.product_id}`}>
        <div className="relative aspect-[16/9] overflow-hidden bg-black/40">
          {meta.image && (
            <img src={meta.image} alt={product.product_name}
                 className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-2">
            <div>
              <h3 className="font-syne font-bold text-lg text-white leading-tight">{product.product_name}</h3>
              <p className="font-mono text-[10px] text-white/60 mt-0.5">v{product.version}</p>
            </div>
            <ChevronDown size={18} className={`text-white/70 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </button>

      {/* Quick row — license count + download, always visible */}
      <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 font-mono text-[10px] text-white/50">
          <Key size={12} className="text-orange-400" />
          {activeLicenses.length} aktive Lizenz{activeLicenses.length === 1 ? '' : 'en'}
          {product.licenses.some(l => l.granted) && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-400/10 text-orange-300">
              <Gift size={9} /> Freigeschaltet
            </span>
          )}
        </div>
        {product.download_url && (
          <ZxpLink href={product.download_url}
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest text-black transition hover:scale-[1.03]"
             style={{ background: 'var(--primary, #F97316)' }}
             testid={`dash-download-${product.product_id}`}>
            <Download size={12} /> v{product.version}
          </ZxpLink>
        )}
      </div>

      {/* Expanded detail */}
      {open && (
        <div className="px-4 pb-4 space-y-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          {meta.tagline && (
            <p className="text-sm text-white/60 pt-4">{meta.tagline}</p>
          )}

          {/* License keys */}
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mb-2">
              Deine Lizenz{product.licenses.length === 1 ? '' : 'en'}
            </p>
            <div className="space-y-2">
              {product.licenses.map((l, i) => <LicenseChip key={i} license={l} />)}
            </div>
          </div>

          {/* Shop link — upsell / view details */}
          <a href={shopUrl}
             className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white transition"
             data-testid={`dash-shop-${product.product_id}`}>
            <ExternalLink size={12} /> Im Shop ansehen
          </a>
        </div>
      )}
    </div>
  );
}

function LicenseChip({ license }) {
  const [copied, setCopied] = useState(false);
  const k = license.license_key;
  const deactivated = license.status === 'deactivated';
  const onCopy = () => {
    if (!k) return;
    navigator.clipboard?.writeText(k).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };
  return (
    <div className={`flex items-center gap-2 p-2.5 rounded-lg ${deactivated ? 'opacity-60' : ''}`}
         style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <Key size={12} className={deactivated ? 'text-white/30' : 'text-orange-400'} />
      <code className={`flex-1 font-mono text-[11px] tracking-wider truncate ${deactivated ? 'text-white/40 line-through' : 'text-white'}`}>
        {k}
      </code>
      <span className="font-mono text-[8px] uppercase tracking-widest text-white/30">{license.license_type}</span>
      {deactivated && (
        <span className="px-1.5 py-0.5 rounded text-[8px] font-mono uppercase tracking-widest bg-amber-500/15 text-amber-300">deaktiviert</span>
      )}
      <button onClick={onCopy} disabled={!k || deactivated}
              className="p-1 rounded hover:bg-white/10 disabled:opacity-30"
              title="Lizenz kopieren">
        {copied ? <CheckCircle2 size={12} className="text-emerald-400" /> : <LinkIcon size={12} className="text-white/50" />}
      </button>
    </div>
  );
}

function UpsellCard({ productId }) {
  const meta = PRODUCT_META[productId] || {};
  const name = {
    'studio-flow': 'Studio Flow', 'media-flow': 'Media Flow',
    'instant-grade': 'Instant Grade', 'instant-comp': 'Instant Comp',
    'instant-curve': 'Instant Curve', 'bounce-flow': 'Bounce Flow', 'shake-flow': 'Shake Flow', 'glitch-flow': 'Glitch Flow', 'transition-flow': 'Transition Flow', 'text-flow': 'Text Flow', 'grid-flow': 'Grid Flow', 'carousel-flow': 'Carousel Flow', 'shape-flow': 'Shape Flow', 'background-flow': 'Background Flow', 'instant-ui-kit': 'Instant UI Kit',
  }[productId] || productId;
  return (
    <a href={`/shop/${productId}`}
       className="monolith-glass overflow-hidden flex flex-col group"
       data-testid={`dash-upsell-${productId}`}>
      <div className="relative aspect-[16/9] overflow-hidden bg-black/40">
        {meta.image && (
          <img src={meta.image} alt={name}
               className="w-full h-full object-cover opacity-70 transition-all duration-500 group-hover:opacity-100 group-hover:scale-105" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute bottom-3 left-4 right-4">
          <h3 className="font-syne font-bold text-base text-white leading-tight">{name}</h3>
          {meta.tagline && <p className="font-mono text-[9px] text-white/60 mt-1 truncate">{meta.tagline}</p>}
        </div>
      </div>
      <div className="p-3 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">Im Shop ansehen</span>
        <ArrowRight size={14} className="text-orange-400 transition-transform group-hover:translate-x-1" />
      </div>
    </a>
  );
}

/* ===== Searchable / sortable list sections (customer + partner) =============
 * Thin wrappers around useListControls + ListToolbar so the customer's orders/
 * licences and the partner's sales get search + sort (+ status filter) instead
 * of a static dump. Rows keep their original markup. */
function NoResults() {
  return <Empty label="Keine Treffer fuer Suche/Filter." />;
}

function CustomerOrdersList({ orders, products }) {
  const ctl = useListControls({
    items: orders || [],
    search: (o) => `${o.product_name || ''} ${o.order_number || ''}`,
    sorts: {
      newest:  { label: 'Neueste zuerst', cmp: (a, b) => (b.created_at || '').localeCompare(a.created_at || '') },
      oldest:  { label: 'Älteste zuerst', cmp: (a, b) => (a.created_at || '').localeCompare(b.created_at || '') },
      amount:  { label: 'Betrag ↓',       cmp: (a, b) => (b.amount || 0) - (a.amount || 0) },
      product: { label: 'Produkt A–Z',    cmp: (a, b) => (a.product_name || '').localeCompare(b.product_name || '', 'de') },
    },
    defaultSort: 'newest',
    storageKey: 'aether_cust_orders',
  });
  return (
    <Section title="Deine Bestellungen">
      {(!orders || orders.length === 0) ? (
        <Empty label="Noch keine Bestellungen." />
      ) : (
        <>
          <ListToolbar controls={ctl} placeholder="Produkt oder #Bestellnr. suchen …" />
          {ctl.result.length === 0 ? <NoResults /> : (
            <div className="space-y-2">
              {ctl.result.map((o, i) => (
                <OrderRow key={o.order_number || i} order={o} products={products} />
              ))}
            </div>
          )}
        </>
      )}
    </Section>
  );
}

/* Eine Bestellung, komplett: was gekauft wurde, der Schlüssel dazu, der Download
   und die Rechnung. Vorher lagen Kauf und Schlüssel in zwei getrennten Listen —
   wer den Schlüssel brauchte, musste die Ansicht wechseln. */
function OrderRow({ order, products }) {
  const { authFetch } = useAetherAuth();
  const [kopiert, setKopiert] = useState(false);
  const [rechnungLaeuft, setRechnungLaeuft] = useState(false);
  const key = order.license_key;
  const produkt = (products || []).find(p => p.product_id === order.product_id);

  // Die Rechnung wird erst beim Klick geholt: bei Altbestellungen steht sie noch
  // nicht an der Bestellung, der Server zieht sie dann von Stripe nach.
  const rechnungOeffnen = async () => {
    setRechnungLaeuft(true);
    try {
      const res = await authFetch(`/api/me/invoice/${encodeURIComponent(order.order_number)}`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { window.alert(d.detail || 'Rechnung konnte nicht geladen werden.'); return; }
      window.open(d.invoice_pdf || d.invoice_url, '_blank', 'noopener');
    } finally { setRechnungLaeuft(false); }
  };

  const kopieren = () => {
    if (!key) return;
    navigator.clipboard?.writeText(key).then(() => {
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1500);
    }).catch(() => {});
  };

  return (
    <div className="monolith-glass p-4 space-y-3" data-testid={`order-${order.order_number || order.product_id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-white truncate">{order.product_name || '—'}</span>
            {order.order_number && <code className="font-mono text-[9px] text-white/40 bg-white/5 px-1.5 py-0.5 rounded">#{order.order_number}</code>}
            {order.invoice_number && <code className="font-mono text-[9px] text-white/40 bg-white/5 px-1.5 py-0.5 rounded">Rg. {order.invoice_number}</code>}
          </div>
          <p className="mt-1 font-mono text-[10px] text-white/40">{fmtDate(order.created_at)}</p>
        </div>
        <p className="font-mono text-sm text-white">
          {order.granted ? 'Freigeschaltet' : fmtMoney(order.amount, order.currency)}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-white/5">
        <span className="font-mono text-[9px] uppercase tracking-widest text-white/35">Schlüssel</span>
        <code className="font-mono text-[11px] px-2 py-1 rounded select-all text-orange-300 bg-orange-300/10">
          {key || '—'}
        </code>
        {produkt?.version && <span className="font-mono text-[10px] text-white/35">v{produkt.version}</span>}

        <div className="flex items-center gap-2 ml-auto">
          {key && (
            <button
              onClick={kopieren}
              className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 liquid-glass hover:bg-white/10 text-white/60 hover:text-white"
              title="Lizenzschlüssel kopieren"
            >
              {kopiert ? <CheckCircle2 size={12} className="text-emerald-400" /> : <LinkIcon size={12} />}
              {kopiert ? 'Kopiert' : 'Kopieren'}
            </button>
          )}
          {produkt?.download_url && (
            <ZxpLink
              href={produkt.download_url}
              className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 liquid-glass hover:bg-white/10 text-white/60 hover:text-white"
            >
              <Download size={12} /> Download
            </ZxpLink>
          )}
          <button
            onClick={rechnungOeffnen}
            disabled={rechnungLaeuft}
            className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 liquid-glass hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-40"
            title={order.invoice_number ? `Rechnung ${order.invoice_number}` : 'Rechnung öffnen'}
          >
            <ExternalLink size={12} /> {rechnungLaeuft ? 'Öffnet' : 'Rechnung'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PartnerSalesList({ orders }) {
  const ctl = useListControls({
    items: orders || [],
    search: (o) => `${o.product_name || ''} ${o.order_number || ''}`,
    sorts: {
      newest:     { label: 'Neueste zuerst', cmp: (a, b) => (b.created_at || '').localeCompare(a.created_at || '') },
      commission: { label: 'Provision ↓',    cmp: (a, b) => (b.commission || 0) - (a.commission || 0) },
      amount:     { label: 'Betrag ↓',       cmp: (a, b) => (b.amount || 0) - (a.amount || 0) },
      product:    { label: 'Produkt A–Z',    cmp: (a, b) => (a.product_name || '').localeCompare(b.product_name || '', 'de') },
    },
    defaultSort: 'newest',
    storageKey: 'aether_partner_sales',
  });
  return (
    <Section title="Letzte Verkäufe">
      {(!orders || orders.length === 0) ? (
        <Empty label="Über deinen Code wurden noch keine Bestellungen abgeschlossen." />
      ) : (
        <>
          <ListToolbar controls={ctl} placeholder="Produkt oder #Bestellnr. suchen …" />
          {ctl.result.length === 0 ? <NoResults /> : (
            <div className="space-y-2">
              {ctl.result.map((o, i) => (
                <div key={o.order_number || i} className="monolith-glass p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-white truncate">{o.product_name || '—'}</span>
                      {o.order_number && <code className="font-mono text-[9px] text-white/40 bg-white/5 px-1.5 py-0.5 rounded">#{o.order_number}</code>}
                    </div>
                    <p className="mt-1 font-mono text-[10px] text-white/40">{relAgo(o.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-white">{fmtMoney(o.amount)}</p>
                    <p className="font-mono text-[10px] text-orange-300">{fmtMoney(o.commission)} Provision</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Section>
  );
}

/* Partner library — the partner's OWN owned/granted plugins with a download
 * link + copyable licence key. Closes the gap where partners were granted real
 * licences they could never see anywhere. Data from build_library_for_email. */
function PartnerLibrary({ items }) {
  return (
    <Section title="Meine Produkte & Lizenzen">
      {(!items || items.length === 0) ? (
        <Empty label="Du besitzt noch keine Extensions. Gekaufte oder freigeschaltete Produkte erscheinen hier." />
      ) : (
        <div className="space-y-2">
          {items.map((it) => <PartnerLibraryRow key={it.product_id} item={it} />)}
        </div>
      )}
    </Section>
  );
}

function PartnerLibraryRow({ item }) {
  const [copied, setCopied] = useState(false);
  const key = item.license_key;
  const copy = () => {
    if (!key) return;
    navigator.clipboard?.writeText(key).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };
  return (
    <div className="monolith-glass p-4 flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-white truncate">{item.product_name}</span>
          <span className="font-mono text-[9px] text-white/40">v{item.version}</span>
          {item.granted && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-400/10 text-orange-300 text-[9px] font-mono uppercase tracking-widest">
              <Gift size={9} /> Freigeschaltet
            </span>
          )}
        </div>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <code className="font-mono text-[11px] px-2 py-0.5 rounded text-orange-300 bg-orange-300/10 select-all">{key || '—'}</code>
          <button onClick={copy} disabled={!key} className="p-1 rounded hover:bg-white/10 disabled:opacity-30" title="Key kopieren">
            {copied ? <CheckCircle2 size={12} className="text-emerald-400" /> : <LinkIcon size={12} className="text-white/50" />}
          </button>
        </div>
      </div>
      {item.download_url && (
        <a href={item.download_url}
           className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest text-black transition hover:scale-[1.03]"
           style={{ background: 'var(--primary, #F97316)' }}>
          <Download size={12} /> v{item.version}
        </a>
      )}
    </div>
  );
}


function OrdersList({ orders, showCustomer = false }) {
  if (!orders || orders.length === 0) return <Empty label="Noch keine bezahlten Bestellungen" />;
  return (
    <div className="space-y-2">
      {orders.map((o, i) => (
        <div key={o.order_number || i} className="monolith-glass p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-white truncate">{o.product_name || '—'}</span>
              {o.order_number && <code className="font-mono text-[9px] text-white/40 bg-white/5 px-1.5 py-0.5 rounded">#{o.order_number}</code>}
            </div>
            <div className="mt-1 flex items-center gap-3 font-mono text-[10px] text-white/40 flex-wrap">
              {showCustomer && o.customer_email && <span className="flex items-center gap-1"><Mail size={10} />{o.customer_email}</span>}
              <span>{relAgo(o.created_at)}</span>
            </div>
          </div>
          <p className="font-mono text-sm text-white">{fmtMoney(o.amount)}</p>
        </div>
      ))}
    </div>
  );
}

function NewUsersList({ users }) {
  if (!users || users.length === 0) return <Empty label="Noch keine Nutzer" />;
  const Icon = (role) => role === 'admin' ? Crown : role === 'partner' ? LinkIcon : Users;
  const accent = (role) => role === 'admin' ? 'text-amber-400' : role === 'partner' ? 'text-sky-400' : 'text-violet-400';
  const label = (role) => role === 'admin' ? 'Admin' : role === 'partner' ? 'Partner' : 'Kunde';
  return (
    <div className="space-y-2">
      {users.map((u) => {
        const I = Icon(u.role);
        return (
          <div key={u.id} className="monolith-glass p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-lg bg-white/5 shrink-0">
                <I size={14} className={accent(u.role)} />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{u.name || u.email}</p>
                <p className="font-mono text-[10px] text-white/40 truncate">{u.email}</p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-white/5 ${accent(u.role)}`}>{label(u.role)}</span>
              <p className="font-mono text-[10px] text-white/40 mt-1">{relAgo(u.created_at)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TopProductsList({ products, mode = 'revenue' }) {
  return (
    <Section title={mode === 'commission' ? 'Deine Top-Produkte' : 'Top-Produkte (Umsatz)'}>
      {(!products || products.length === 0) ? (
        <Empty label="Noch keine Verkäufe" />
      ) : (
        <div className="space-y-2">
          {products.map(p => (
            <div key={p.product_name} className="monolith-glass p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{p.product_name}</p>
                <p className="font-mono text-[10px] text-white/40">{p.sales} Verkauf{p.sales !== 1 ? 'e' : ''}</p>
              </div>
              <p className="font-mono text-sm text-white">{fmtMoney(mode === 'commission' ? p.commission : p.revenue)}</p>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function RecentLicensesList({ licenses }) {
  if (!licenses || licenses.length === 0) {
    return <Empty label="Noch keine Lizenzen erstellt" />;
  }
  return (
    <div className="space-y-2">
      {licenses.map((l) => (
        <div key={l.id || l.key} className={`monolith-glass p-4 flex items-center justify-between gap-3 flex-wrap ${!l.is_active ? 'opacity-60' : ''}`}>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <code className={`font-mono text-[11px] px-2 py-0.5 rounded ${l.is_active ? 'text-orange-300 bg-orange-300/10' : 'text-white/40 bg-white/5 line-through'}`}>{l.key}</code>
              {!l.is_active && (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-amber-500/15 text-amber-300">Deaktiviert</span>
              )}
              {l.key_type && (
                <span className="text-[9px] font-mono uppercase tracking-widest text-white/40">{l.key_type}</span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-3 font-mono text-[10px] text-white/40 flex-wrap">
              {l.product_name && <span className="text-white/60">{l.product_name}</span>}
              {l.customer_email && <span className="flex items-center gap-1"><Mail size={10} />{l.customer_email}</span>}
              <span>{relAgo(l.created_at)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TopAffiliatesList({ affiliates }) {
  return (
    <Section title="Top-Affiliates (Provisionen)">
      {(!affiliates || affiliates.length === 0) ? (
        <Empty label="Noch keine Affiliate-Conversions" />
      ) : (
        <div className="space-y-2">
          {affiliates.map(a => (
            <div key={a.affiliate_code} className="monolith-glass p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex items-center gap-3">
                <LinkIcon size={14} className="text-orange-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{a.name}</p>
                  <code className="font-mono text-[10px] text-orange-300 bg-orange-300/10 px-1.5 py-0.5 rounded">{a.affiliate_code}</code>
                </div>
              </div>
              <div className="text-right">
                <p className="font-mono text-sm text-white">{fmtMoney(a.commission)}</p>
                <p className="font-mono text-[10px] text-white/40">{a.sales} Verkauf{a.sales !== 1 ? 'e' : ''}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ============================== SPARKLINE ============================== */
function RevenueSparkline({ series, title, valueKey = 'revenue' }) {
  if (!series || series.length === 0) {
    return <Section title={title}><Empty label="Noch keine Daten." /></Section>;
  }
  const values = series.map(p => Number(p[valueKey]) || 0);
  const max = Math.max(...values, 1);
  const total = values.reduce((a, b) => a + b, 0);
  const peak = Math.max(...values);
  const peakIdx = values.indexOf(peak);
  return (
    <Section title={title}>
      <div className="monolith-glass p-5">
        <div className="flex items-end gap-1.5 h-32">
          {series.map((p, i) => {
            const v = Number(p[valueKey]) || 0;
            const h = Math.max(2, (v / max) * 100);
            const day = new Date(p.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
            const isPeak = i === peakIdx && peak > 0;
            return (
              <div key={p.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${day} · ${fmtMoney(v)}`}>
                <div
                  className={`w-full rounded-t transition-colors ${isPeak ? 'bg-orange-400' : 'bg-white/15 group-hover:bg-white/30'}`}
                  style={{ height: `${h}%` }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Summe</p>
            <p className="font-mono text-sm text-white">{fmtMoney(total)}</p>
          </div>
          <div className="text-right">
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Spitze</p>
            <p className="font-mono text-sm text-orange-300">{fmtMoney(peak)}</p>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ============================== ATOMS ============================== */
function BigStat({ label, value, icon: Icon, accent = 'text-white', subtitle }) {
  return (
    <div className="monolith-glass p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={accent} />
        <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">{label}</span>
      </div>
      <p className="text-xl font-semibold text-white">{value}</p>
      {subtitle && <p className="font-mono text-[10px] text-white/40 mt-1">{subtitle}</p>}
    </div>
  );
}

function Section({ title, cta, children }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40">{title}</h3>
        {cta && (
          <button onClick={cta.onClick} className="font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:text-white flex items-center gap-1">
            {cta.label} <ArrowRight size={10} />
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function Empty({ label }) {
  return (
    <div className="monolith-glass p-8 text-center text-white/30 text-xs font-mono uppercase tracking-widest">
      {label}
    </div>
  );
}

function NothingVisibleHint() {
  return (
    <div className="monolith-glass p-8 text-center">
      <Activity size={20} className="text-white/30 mx-auto mb-3" />
      <p className="text-white/50 text-sm">In diesem Tab ist gerade nichts sichtbar.</p>
      <p className="text-white/30 text-xs mt-1">Über das Zahnrad oben rechts kannst du Widgets wieder einblenden.</p>
    </div>
  );
}

function CopyLinkBtn({ code }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}?ref=${code}`;
  const onCopy = () => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };
  return (
    <button onClick={onCopy} className="px-4 py-2 rounded-lg text-xs flex items-center gap-2 liquid-glass hover:bg-white/10 text-white/60 hover:text-white">
      {copied ? <CheckCircle2 size={13} className="text-emerald-400" /> : <LinkIcon size={13} />}
      {copied ? 'Kopiert' : 'Link kopieren'}
    </button>
  );
}

/* ====================================================================
   AdminProdukte — eigene Seite in der Leiste, kein Reiter im Dashboard.
   Was verkauft wird, mit den Zahlen dahinter. Nur Lesen: Name, Preis und
   Beschreibung stehen fest im Code.
   ==================================================================== */
function AdminProdukte() {
  const { authFetch } = useAetherAuth();
  const [produkte, setProdukte] = useState(null);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    let weg = false;
    (async () => {
      try {
        const res = await authFetch('/api/admin/products');
        const d = await res.json();
        if (weg) return;
        if (!res.ok) throw new Error(d.detail || 'Konnte nicht geladen werden');
        setProdukte(d.produkte || []);
      } catch (e) { if (!weg) setFehler(e.message); }
    })();
    return () => { weg = true; };
  }, [authFetch]);

  if (fehler) return <div className="p-4 rounded-lg bg-red-500/10 text-red-300 text-sm">{fehler}</div>;
  if (!produkte) return <div className="py-16 text-center font-mono text-xs text-white/30">Lade Produkte …</div>;

  return (
    <div className="space-y-4" data-testid="admin-produkte">
      <p className="text-white/45 text-sm">
        {produkte.length} Produkte · Name, Preis und Beschreibung stehen fest im Code.
        Version und Download-Link änderst du unter E-Mails.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {produkte.map((p) => {
          const abweichung = p.version_live && p.version_code && p.version_live !== p.version_code;
          return (
            <div key={p.product_id} className="monolith-glass p-5 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-white font-medium">{p.name}</p>
                  <p className="text-white/45 text-sm mt-0.5">{p.description}</p>
                </div>
                <span className="font-mono text-sm text-white shrink-0">
                  {p.price > 0 ? fmtMoney(p.price, p.currency) : 'Gratis'}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/5 text-center">
                <div>
                  <div className="font-mono text-lg text-white tabular-nums">{p.verkauft}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-white/35">verkauft</div>
                </div>
                <div>
                  <div className="font-mono text-lg text-white tabular-nums">{fmtMoney(p.umsatz, p.currency)}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-white/35">Umsatz</div>
                </div>
                <div>
                  <div className="font-mono text-lg text-white tabular-nums">{p.freigeschaltet}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-white/35">freigeschaltet</div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/5">
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/35">Version</span>
                <code className="font-mono text-[11px] px-2 py-0.5 rounded bg-white/5 text-white/70">
                  {p.version_live || p.version_code || '—'}
                </code>
                {abweichung && (
                  <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300"
                        title={`Im Code steht v${p.version_code}, ausgeliefert wird v${p.version_live}`}>
                    Code: v{p.version_code}
                  </span>
                )}
                {p.download_url ? (
                  <a href={p.download_url} target="_blank" rel="noreferrer"
                     className="ml-auto font-mono text-[10px] uppercase tracking-widest text-white/45 hover:text-orange-300 inline-flex items-center gap-1.5">
                    <Download size={11} /> Build
                  </a>
                ) : (
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-amber-300/70">kein Build</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
